/**
 * The published contract's identity and its compatibility policy.
 *
 * WHY A VERSION AT ALL
 * --------------------
 * A card is not private state — other applications (Guide, Commend, Resolve, and
 * whatever comes next) read these documents to render products. Until this
 * folder existed the only version marker in the system was the *storage key*,
 * `gms.productCards.v1`, which a consumer holding a card blob never sees. A
 * service handed `{"id":"card-1","orientation":"vertical",…}` had no way to ask
 * "which shape is this?" — it could only guess from the keys it happened to
 * recognise, and guess wrong the first time the shape moved.
 *
 * So the version travels *inside the document*, on both the envelope and each
 * card, and every card is self-describing when extracted from its collection.
 *
 * THE COMPATIBILITY POLICY (the part consumers need in writing)
 * ------------------------------------------------------------
 * Within a major version, only these changes are allowed:
 *
 *   - adding an OPTIONAL property, whose absence has a documented default;
 *   - adding a member to an enum that consumers are told to treat leniently
 *     (unknown members must be ignored, never rejected);
 *   - relaxing a constraint (a longer `maxLength`, a wider numeric range).
 *
 * Anything else — removing a property, making an optional one required,
 * renaming, narrowing a range, changing a type — is a MAJOR bump. Consumers may
 * therefore pin `schemaVersion === 1` and know their reader keeps working.
 *
 * A reader that sees a HIGHER version than it understands must refuse the
 * document rather than parse the subset it recognises: the parts it recognises
 * may have changed meaning. `decode.ts` implements exactly that.
 *
 * READER RULES (every consumer, in writing):
 *
 *   1. IGNORE unknown properties — never reject a document for carrying a key
 *      you do not know. The published schema deliberately omits
 *      `additionalProperties: false` so a vendored copy keeps validating
 *      legally newer documents; do not add it back when validating input.
 *   2. IGNORE unknown enum members (a fieldId or region you cannot map: skip
 *      that component, render the rest).
 *   3. REFUSE the whole document only when `schemaVersion` is higher than you
 *      understand.
 *   4. An absent optional key means its documented default — the `default`
 *      annotations in the schema are normative.
 */

/** The contract's major version. Bumped only for a breaking change (see above). */
export const PRODUCT_CARD_SCHEMA_VERSION = 1;

/**
 * The schema's canonical identifier, carried as `$schema` on the envelope.
 *
 * A URL by JSON Schema convention. It is an *identifier*, not a fetch target —
 * nothing in this module or in any consumer is expected to dereference it, and
 * the authoritative copy is the `product-card.v1.schema.json` file that ships
 * beside this one.
 */
export const PRODUCT_CARD_SCHEMA_ID =
  'https://schemas.compatio.ai/product-cards/product-card.v1.json';

/** `kind` on the envelope: what the document *is*, independent of its version. */
export const PRODUCT_CARD_COLLECTION_KIND = 'product-card-collection';

/** Suggested `Content-Type` for an API that serves these documents. */
export const PRODUCT_CARD_MEDIA_TYPE =
  'application/vnd.compatio.product-card+json; version=1';
